
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from './ui/use-toast';
import { Copy, Globe, Link, Lock } from 'lucide-react';
import { SharingSettings } from '../types';
import { createSharingSettings } from '../utils/sharingService';
import { updateCampaignSharing } from '../utils/firebase';

interface SharingControlsProps {
  campaignId: string;
  currentSettings?: SharingSettings;
  campaignName: string;
  onUpdate: (settings: SharingSettings) => void;
}

const SharingControls: React.FC<SharingControlsProps> = ({
  campaignId,
  currentSettings,
  campaignName,
  onUpdate
}) => {
  const [settings, setSettings] = useState<SharingSettings>(
    currentSettings || { visibility: 'private', view_count: 0 }
  );
  const [publicTitle, setPublicTitle] = useState(
    currentSettings?.public_title || campaignName
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const handleVisibilityChange = async (visibility: 'private' | 'shareable' | 'public') => {
    setIsUpdating(true);
    
    try {
      const newSettings = createSharingSettings(
        visibility,
        visibility === 'public' ? publicTitle : undefined
      );
      
      await updateCampaignSharing(campaignId, newSettings);
      setSettings(newSettings);
      onUpdate(newSettings);
      
      toast({
        title: 'Sharing updated',
        description: `Campaign is now ${visibility}`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update sharing settings',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const copyShareLink = () => {
    if (settings.share_code) {
      const shareUrl = `${window.location.origin}/shared/${settings.share_code}`;
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Link copied',
        description: 'Share link copied to clipboard'
      });
    }
  };

  const getShareUrl = () => {
    if (!settings.share_code) return '';
    return `${window.location.origin}/shared/${settings.share_code}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Sharing Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Button
            variant={settings.visibility === 'private' ? 'default' : 'outline'}
            onClick={() => handleVisibilityChange('private')}
            disabled={isUpdating}
            className="justify-start"
          >
            <Lock className="w-4 h-4 mr-2" />
            Private - Only you can access
          </Button>
          
          <Button
            variant={settings.visibility === 'shareable' ? 'default' : 'outline'}
            onClick={() => handleVisibilityChange('shareable')}
            disabled={isUpdating}
            className="justify-start"
          >
            <Link className="w-4 h-4 mr-2" />
            Shareable - Anyone with link can access
          </Button>
          
          <Button
            variant={settings.visibility === 'public' ? 'default' : 'outline'}
            onClick={() => handleVisibilityChange('public')}
            disabled={isUpdating}
            className="justify-start"
          >
            <Globe className="w-4 h-4 mr-2" />
            Public - Listed publicly
          </Button>
        </div>

        {settings.visibility === 'public' && (
          <div className="space-y-2">
            <Label htmlFor="publicTitle">Public Title</Label>
            <Input
              id="publicTitle"
              value={publicTitle}
              onChange={(e) => setPublicTitle(e.target.value)}
              placeholder="Enter public title..."
            />
          </div>
        )}

        {(settings.visibility === 'shareable' || settings.visibility === 'public') && settings.share_code && (
          <div className="space-y-2">
            <Label>Share Link</Label>
            <div className="flex gap-2">
              <Input
                value={getShareUrl()}
                readOnly
                className="font-mono text-sm"
              />
              <Button onClick={copyShareLink} variant="outline" size="sm">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {settings.view_count !== undefined && settings.view_count > 0 && (
          <div className="text-sm text-muted-foreground">
            Views: {settings.view_count}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SharingControls;
